import {type LocationSnapshot} from '../../pages/my-vessels-v2/listing/hooks/use-location-snapshot'
import {type AxiosRequestConfig} from 'axios'
import {REST} from '../../api'
import {isArray, isString} from 'lodash'
import type {Location as VesselResource} from '../../store/state/locations/state'
import type {
    CreateOtsmVesselResponse,
    OtsmVesselLookupResponse,
    OtsmVesselRequest,
    OtsmVesselRequestError,
} from './types/vessels.types'

export type {VesselResource as Vessel}

const LOCATION_SNAPSHOT_URL = '/medulla-onboard/api/v1/config/snapshot'
const LOCATION_SNAPSHOT_LOAD_URL = '/medulla-onboard/api/v1/config/snapshot/load'
const OTSM_VESSELS_ENDPOINT = '/vessels/api/v1/nodes/locations/otsm'
const VESSELS_URL = '/vessels/api/v1/nodes/locations'

export async function fetchLocationSnapshots() {
    try {
        const response = await REST.get(LOCATION_SNAPSHOT_URL)

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.data
    } catch (error) {
        throw new Error(
            `Failed to fetch location snapshots: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function updateLocationSnapshot(snapshot: LocationSnapshot) {
    try {
        const response = await REST.post(LOCATION_SNAPSHOT_LOAD_URL, snapshot)

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.data
    } catch (error) {
        throw new Error(
            `Failed to update location snapshot: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export function isOtsmVesselRequestError(v: unknown): v is OtsmVesselRequestError {
    const u = v as OtsmVesselRequestError
    return isString(u.message) && isArray(u.errors)
}

export async function validateOtsmOnlyVessels(
    body: OtsmVesselRequest[],
    config?: AxiosRequestConfig,
) {
    try {
        const res = await REST.post(`${OTSM_VESSELS_ENDPOINT}/validate`, body, config)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to validate OTSM vessels: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function createOtsmOnlyVessel(body: OtsmVesselRequest, config?: AxiosRequestConfig) {
    try {
        const res = await REST.post<CreateOtsmVesselResponse>(OTSM_VESSELS_ENDPOINT, body, config)
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to create OTSM vessel: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function lookupOtsmOnlyVessel(imos: string[], config?: AxiosRequestConfig) {
    try {
        const params = new URLSearchParams()
        imos.forEach((imo) => {
            params.append('imos', imo)
        })

        const res = await REST.get<OtsmVesselLookupResponse>(`${OTSM_VESSELS_ENDPOINT}/lookup`, {
            ...config,
            params,
        })
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to lookup OTSM vessels: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

type FetchVesselsParams = Record<string, string | string[] | undefined>

export async function fetchVessels(params: FetchVesselsParams = {}): Promise<VesselResource[]> {
    try {
        const query = new URLSearchParams()

        for (const key in params) {
            const value = params[key]
            if (Array.isArray(value)) {
                value.forEach((v) => {
                    query.append(key, v)
                })
            } else if (value !== undefined) {
                query.set(key, value)
            }
        }

        const res = await REST.get<VesselResource[]>(`${VESSELS_URL}?${query.toString()}`)
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch vessels: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}
